import '@increasecard/typed-components/index.css';

import React, { Suspense } from 'react';
import { createGlobalStyle } from 'styled-components';
import { ThemeProvider } from '@increasecard/typed-components';
import { BaseProvider, Environment, useEventTracker } from '@increasecard/increase-core';
import { BrowserRouter, Route, Switch, useLocation } from 'react-router-dom';
import { config } from './config';
import { routes } from './routes';
import { ErrorTrackerLoader } from './components/ErrorTracker';
import LoadingIndicator from './components/LoadingIndicator';
import { Layout } from './components/Layout';
import { PrivacyPolicy, TermsAndConditions } from './containers/Legal';

const GlobalStyle = createGlobalStyle`
  body {
    margin: 0;
    padding: 0;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
`;

const trackEvents: boolean = import.meta.env.REACT_APP_EVENTS_TRACK === 'true';
const trackErrors: boolean = import.meta.env.VITE_ENV === 'production';
const environment = import.meta.env.VITE_ENV as Environment | undefined;
const mixpanelToken: string = config.mixpanelToken;
const rollbarToken: string = config.rollbarToken;

function RouteChangeTracker() {
  const location = useLocation();
  const eventTracker = useEventTracker();
  React.useEffect(() => {
    eventTracker.track('Page View', { path: location.pathname });
  }, [location, eventTracker]);

  return null;
}

function App() {
  return (
    <ThemeProvider productName="count">
      <BrowserRouter>
        <GlobalStyle />
        <Switch>
          <Route component={TermsAndConditions} exact path={routes.legal.termsAndConditions} />
          <Route component={PrivacyPolicy} exact path={routes.legal.privacyPolicy} />
          <Route>
            <BaseProvider
              settings={{
                environment: environment || 'development',
                productName: 'platform',
                eventTracking: { enabled: trackEvents, mixpanelToken },
                errorTracking: { enabled: trackErrors, rollbarToken },
              }}
            >
              <ErrorTrackerLoader />
              <RouteChangeTracker />
              <Suspense
                fallback={
                  <div style={{ height: '100vh', width: '100vw' }}>
                    <LoadingIndicator />
                  </div>
                }
              >
                <Layout />
              </Suspense>
            </BaseProvider>
          </Route>
        </Switch>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
